import type { Color, Move, PieceSymbol, Square } from 'chess.js'

import { Chess } from 'chess.js'

export type BoardPiece = {
    id: string
    type: PieceSymbol
    color: Color
    square: Square
    captured: boolean
}

export type ReplayStep = {
    move: Move
    pieces: BoardPiece[]
    check: boolean
    mate: boolean
}

export const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const
export const RANKS = ['8', '7', '6', '5', '4', '3', '2', '1'] as const

export const PIECE_NAMES: Record<PieceSymbol, string> = {
    p: 'pawn',
    n: 'knight',
    b: 'bishop',
    r: 'rook',
    q: 'queen',
    k: 'king'
}

export const COLOR_NAMES: Record<Color, string> = {
    w: 'white',
    b: 'black'
}

export const toSquare = (fileIndex: number, rowIndex: number): Square => {
    return `${FILES[fileIndex]}${RANKS[rowIndex]}` as Square
}

export const squareToCoords = (square: Square): { file: number, row: number } => {
    return {
        file: square.charCodeAt(0) - 97,
        row: 8 - Number(square[1])
    }
}

export const piecesFromFen = (fen: string): BoardPiece[] => {
    const chess = new Chess(fen)

    return chess.board().flat().flatMap(cell => {
        if (!cell) return []

        return [{
            id: `${cell.color}${cell.type}-${cell.square}`,
            type: cell.type,
            color: cell.color,
            square: cell.square,
            captured: false
        }]
    })
}

export const applyMove = (pieces: BoardPiece[], move: Move): BoardPiece[] => {
    const next = pieces.map(piece => ({ ...piece }))
    const pieceOn = (square: Square) => next.find(piece => !piece.captured && piece.square === square)

    if (move.isEnPassant()) {
        const victim = pieceOn(`${move.to[0]}${move.from[1]}` as Square)
        if (victim) victim.captured = true
    }
    else if (move.captured) {
        const victim = pieceOn(move.to)
        if (victim) victim.captured = true
    }

    const mover = pieceOn(move.from)

    if (mover) {
        mover.square = move.to
        if (move.promotion) mover.type = move.promotion
    }

    if (move.isKingsideCastle() || move.isQueensideCastle()) {
        const rank = move.from[1]
        const rookFrom = (move.isKingsideCastle() ? `h${rank}` : `a${rank}`) as Square
        const rookTo = (move.isKingsideCastle() ? `f${rank}` : `d${rank}`) as Square
        const rook = pieceOn(rookFrom)
        if (rook) rook.square = rookTo
    }

    return next
}

export const findKing = (pieces: BoardPiece[], color: Color): Square | null => {
    return pieces.find(piece => !piece.captured && piece.type === 'k' && piece.color === color)?.square ?? null
}

export const buildReplay = (moves: string[], startFen?: string): { initial: BoardPiece[], steps: ReplayStep[] } => {
    const chess = new Chess(startFen)
    const initial = piecesFromFen(chess.fen())
    const steps: ReplayStep[] = []

    let pieces = initial

    for (const san of moves) {
        try {
            const move = chess.move(san)
            pieces = applyMove(pieces, move)
            steps.push({
                move,
                pieces,
                check: chess.inCheck(),
                mate: chess.isCheckmate()
            })
        }
        catch {
            break
        }
    }

    return { initial, steps }
}
